import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { FileText, CheckCircle2, Clock, ArrowRight, Loader2, AlertTriangle, PenLine } from "lucide-react";

export default function ClientDocumentsPage() {
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null); // current assignment + template
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const nav = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/me/documents");
            setItems(data);
        } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const pending = items.filter((i) => i.status === "pending");
    const done = items.filter((i) => i.status !== "pending");

    const openFill = async (a) => {
        const { data } = await api.get(`/documents/assignments/${a.id}`);
        setActive(data);
    };

    if (active) return <FillForm data={active} onClose={() => { setActive(null); load(); }} onBack={() => setActive(null)} />;

    return (
        <div className="p-8 max-w-3xl mx-auto space-y-6" data-testid="client-docs-page">
            <div>
                <p className="label-tiny mb-2">/ Onboarding</p>
                <h1 className="font-serif text-5xl">Your documents</h1>
                <p className="text-sm text-[var(--text-muted)] mt-2">
                    Sign your onboarding packet so King can tailor the right program for you.
                </p>
            </div>
            {err && <div className="text-sm text-[var(--danger)]">{err}</div>}
            {loading && <Loader2 size={14} className="animate-spin text-[var(--text-muted)]" />}

            {pending.length > 0 && (
                <div className="border border-[var(--gold)] bg-[var(--surface)] p-5" data-testid="pending-list">
                    <div className="flex items-center gap-2 mb-4 text-[var(--gold-bright)]">
                        <AlertTriangle size={14} />
                        <p className="font-mono text-xs tracking-[0.25em] uppercase">{pending.length} waiting for you</p>
                    </div>
                    <div className="space-y-2">
                        {pending.map((a) => (
                            <button key={a.id} onClick={() => openFill(a)}
                                className="w-full flex items-center justify-between p-4 border border-[var(--border-bright)] hover:border-[var(--gold)] hover:bg-[var(--surface-hover)] text-left"
                                data-testid={`pending-${a.id}`}>
                                <div className="flex items-center gap-3">
                                    <FileText size={16} className="text-[var(--gold)]" />
                                    <span className="font-serif text-xl">{a.template_title}</span>
                                </div>
                                <ArrowRight size={14} className="text-[var(--text-muted)]" />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {pending.length === 0 && !loading && (
                <div className="border border-[var(--border)] bg-[var(--surface)] p-6 text-center text-sm text-[var(--text-muted)]" data-testid="no-pending">
                    <CheckCircle2 size={24} className="text-[var(--gold-bright)] mx-auto mb-3" />
                    You're all caught up. Nothing to sign right now.
                </div>
            )}

            {done.length > 0 && (
                <div>
                    <p className="label-tiny mb-3">/ Completed</p>
                    <div className="border border-[var(--border)] bg-[var(--surface)] divide-y divide-[var(--border)]" data-testid="done-list">
                        {done.map((a) => (
                            <div key={a.id} className="flex items-center justify-between p-4" data-testid={`done-${a.id}`}>
                                <div className="flex items-center gap-3">
                                    <CheckCircle2 size={14} className="text-[var(--gold-bright)]" />
                                    <span className="text-sm">{a.template_title}</span>
                                </div>
                                <span className="font-mono text-xs text-[var(--text-muted)]">{a.signed_at ? new Date(a.signed_at).toLocaleDateString() : ""}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function FillForm({ data, onClose, onBack }) {
    const { user } = useAuth();
    const { assignment, template } = data;
    const [values, setValues] = useState({});
    const [signature, setSignature] = useState(user?.name || "");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    const canSubmit = useMemo(() => {
        for (const f of (template?.fields || [])) {
            if (!f.required) continue;
            const v = values[f.id];
            if (f.type === "acknowledge") { if (v !== true) return false; }
            else if (!String(v || "").trim()) return false;
        }
        if (template?.require_signature !== false && signature.trim().length < 2) return false;
        return true;
    }, [values, signature, template]);

    const submit = async () => {
        setBusy(true); setErr("");
        try {
            await api.post(`/me/documents/${assignment.id}/submit`, {
                data: values,
                signature_name: signature.trim(),
            });
            setDone(true);
        } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    if (done) {
        return (
            <div className="p-8 max-w-2xl mx-auto text-center" data-testid="doc-submitted">
                <CheckCircle2 size={48} className="text-[var(--gold-bright)] mx-auto mb-6" />
                <h1 className="font-serif text-4xl mb-3">Submitted. Thank you.</h1>
                <p className="text-sm text-[var(--text-muted)] mb-6">King has been notified. You'll hear back shortly.</p>
                <button onClick={onClose} className="btn-primary" data-testid="back-to-docs">Back to documents</button>
            </div>
        );
    }

    return (
        <div className="p-8 max-w-3xl mx-auto space-y-6" data-testid="doc-fill-page">
            <button onClick={onBack} className="text-sm text-[var(--text-muted)] hover:text-[var(--gold)]">← All documents</button>
            <div>
                <p className="label-tiny mb-2">/ Please complete</p>
                <h1 className="font-serif text-5xl mb-2">{template.title}</h1>
                {template.description && <p className="text-sm text-[var(--text-muted)]">{template.description}</p>}
            </div>

            <div className="border border-[var(--border)] bg-[var(--surface)] divide-y divide-[var(--border)]">
                {(template.fields || []).map((f) => (
                    <div key={f.id} className="p-5" data-testid={`fill-${f.id}`}>
                        <label className="label-tiny block mb-2">{f.label}{f.required ? " *" : ""}</label>
                        {f.type === "textarea" && (
                            <textarea rows={3} className="input-dark" placeholder={f.placeholder || ""}
                                value={values[f.id] || ""} onChange={(e) => setValues({ ...values, [f.id]: e.target.value })} />
                        )}
                        {f.type === "date" && (
                            <input type="date" className="input-dark"
                                value={values[f.id] || ""} onChange={(e) => setValues({ ...values, [f.id]: e.target.value })} />
                        )}
                        {f.type === "select" && (
                            <select className="input-dark" value={values[f.id] || ""} onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}>
                                <option value="">Choose…</option>
                                {(f.options || []).map((o) => <option key={o}>{o}</option>)}
                            </select>
                        )}
                        {(f.type === "text" || !f.type) && (
                            <input className="input-dark" placeholder={f.placeholder || ""}
                                value={values[f.id] || ""} onChange={(e) => setValues({ ...values, [f.id]: e.target.value })} />
                        )}
                        {f.type === "acknowledge" && (
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input type="checkbox" checked={values[f.id] === true} onChange={(e) => setValues({ ...values, [f.id]: e.target.checked })} className="mt-1" />
                                <span className="text-sm">{f.label}</span>
                            </label>
                        )}
                        {f.help_text && <p className="text-xs text-[var(--text-muted)] mt-2">{f.help_text}</p>}
                    </div>
                ))}
                {template.require_signature !== false && (
                    <div className="p-5 bg-[var(--surface-2)]">
                        <label className="label-tiny block mb-2 flex items-center gap-2"><PenLine size={12} /> Type your full name as signature</label>
                        <input className="input-dark font-serif text-2xl italic"
                            value={signature} onChange={(e) => setSignature(e.target.value)}
                            placeholder="First Last" data-testid="signature-input" />
                        <p className="text-xs text-[var(--text-muted)] mt-2">By typing your name, you agree this constitutes your electronic signature.</p>
                    </div>
                )}
            </div>

            {err && <div className="text-sm text-[var(--danger)] flex items-center gap-2"><AlertTriangle size={14} /> {err}</div>}

            <div className="flex justify-end gap-3">
                <button onClick={onBack} className="btn-ghost">Save & close</button>
                <button onClick={submit} disabled={busy || !canSubmit} className="btn-primary inline-flex items-center gap-2" data-testid="submit-doc">
                    {busy ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <><CheckCircle2 size={14} /> Submit & sign</>}
                </button>
            </div>
        </div>
    );
}
